import { DistributionEntry } from '../../../types';
import { useSubmitDistribution } from '../hooks/useSubmitDistribution';
import { formatNumber } from '../../../../../helpers/NumberFormatter';
import { colors } from '../../../../../colors';
import './index.css';

interface Props {
  changedEntries: DistributionEntry[];
  onClose: () => void;
}

const ConfirmationModal = ({ changedEntries, onClose }: Props) => {
  const { submit, state, progress, error, resetState } =
    useSubmitDistribution();

  const handleConfirm = () => {
    submit(changedEntries);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  return (
    <div className="confirmation-overlay" onClick={handleClose}>
      <div className="confirmation-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="confirmation-title">Confirm Balance Distribution</h3>

        {state === 'idle' && (
          <>
            <ul className="confirmation-list">
              {changedEntries.map((entry) => {
                const pctDelta =
                  entry.currentPercentage - entry.originalPercentage;
                const stableDiff =
                  entry.currentStableBalance - entry.originalStableBalance;
                const assetDiff =
                  entry.currentAssetBalance - entry.originalAssetBalance;

                return (
                  <li key={entry.subscriptionId} className="confirmation-item">
                    <span className="confirmation-item-name">
                      {entry.botFriendlyName}
                    </span>
                    <span className="confirmation-item-change">
                      <span
                        className="confirmation-item-pct"
                        style={{
                          color: pctDelta >= 0 ? colors.green : colors.red,
                        }}
                      >
                        {formatNumber(entry.originalPercentage, 1)}% →{' '}
                        {formatNumber(entry.currentPercentage, 1)}% (
                        {pctDelta >= 0 ? '+' : ''}
                        {formatNumber(pctDelta, 1)}%)
                      </span>
                      {Math.abs(stableDiff) > 0.01 && (
                        <span className="confirmation-item-detail">
                          Stable: {stableDiff >= 0 ? '+' : ''}
                          {formatNumber(stableDiff, 2)} $
                        </span>
                      )}
                      {Math.abs(assetDiff) > 0.001 && (
                        <span className="confirmation-item-detail">
                          Asset: {assetDiff >= 0 ? '+' : ''}
                          {formatNumber(assetDiff, 6)} {entry.baseAsset}
                          {' (real order)'}
                        </span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
            <div className="confirmation-actions">
              <button className="confirmation-btn cancel" onClick={handleClose}>
                Cancel
              </button>
              <button
                className="confirmation-btn confirm"
                onClick={handleConfirm}
              >
                Confirm
              </button>
            </div>
          </>
        )}

        {state === 'submitting' && progress && (
          <>
            <p className="confirmation-progress">
              Processing {progress.currentSub} ({progress.phase} balance)...
              Step {progress.current} of {progress.total}
            </p>
            <div className="confirmation-progress-bar">
              <div
                className="confirmation-progress-fill"
                style={{
                  width: `${(progress.current / progress.total) * 100}%`,
                }}
              />
            </div>
          </>
        )}

        {state === 'error' && (
          <>
            <p className="confirmation-error">{error}</p>
            <div className="confirmation-actions">
              <button className="confirmation-btn cancel" onClick={handleClose}>
                Close
              </button>
            </div>
          </>
        )}

        {state === 'success' && (
          <>
            <p className="confirmation-success">
              Distribution completed successfully.
            </p>
            <div className="confirmation-actions">
              <button
                className="confirmation-btn confirm"
                onClick={handleClose}
              >
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ConfirmationModal;
